// Video carousel section JavaScript functionality
class VideoCarousel extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  connectedCallback() {
    // 当元素连接到DOM时进行基础初始化
  }

  disconnectedCallback() {
    // 清理观察器
    if (this.buttonObserver) {
      this.buttonObserver.disconnect();
      this.buttonObserver = null;
    }

    // 清理按钮保护定时器
    if (this.buttonProtectionInterval) {
      clearInterval(this.buttonProtectionInterval);
      this.buttonProtectionInterval = null;
    }

    // 清理resize事件监听器
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('orientationchange', this.resizeHandler);
      this.resizeHandler = null;
    }

    // 清理Swiper实例
    if (this.mobileSwiper) {
      this.mobileSwiper.destroy(true, true);
      this.mobileSwiper = null;
    }

    // 调试信息清理代码已移除
  }

  init() {
    // 为播放按钮添加点击事件
    this.addEventListener('click', this.handleCardClick.bind(this));
    
    // 延迟初始化，确保DOM完全渲染
    setTimeout(() => {
      // 根据屏幕尺寸选择初始化方式
      if (window.innerWidth >= 768) {
        // 桌面端：只设置轮播功能
        this.setupDesktopCarousel();
      } else {
        // 移动端：只初始化Swiper
        this.initMobileSwiper();
      }
    }, 200);

    // 当页面不可见时暂停所有视频
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAllPlayingVideos();
      }
    });

    // 监听窗口大小变化 - 实时响应屏幕尺寸变化
    let resizeTimeout;
    const handleResize = () => {
      // 清除之前的计时器
      clearTimeout(resizeTimeout);
      
      // 立即清除之前的尺寸缓存
      this.cardDimensions = null;
      
      // 防抖处理，避免频繁重绘
      resizeTimeout = setTimeout(() => {
        // 根据当前屏幕尺寸选择初始化方式
        if (window.innerWidth >= 768) {
          // 桌面端：只设置轮播功能
          this.setupDesktopCarousel();
        } else {
          // 移动端：只初始化Swiper
          this.initMobileSwiper();
        }
        
        // 协调：通知外部边距管理器重新计算
        if (window.marginManager && typeof window.marginManager.applyMargins === 'function') {
          setTimeout(() => window.marginManager.applyMargins(true), 50);
        }
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    
    // 额外监听orientationchange事件（移动设备旋转）
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        handleResize();
      }, 100);
    });

    // 存储resize处理器，用于清理
    this.resizeHandler = handleResize;
  }

  // 设置桌面端轮播功能
  setupDesktopCarousel() {
    if (window.innerWidth < 768) return;

    // 确保移动端Swiper已销毁
    if (this.mobileSwiper) {
      this.mobileSwiper.destroy(true, true);
      this.mobileSwiper = null;
    }

    // 强制应用样式
    this.forceCarouselStyles();
    
    // 强制显示按钮
    this.forceShowButtons();
    
    // 设置轮播逻辑
    this.setupCarouselButtons();

    // 设置按钮保护机制
    this.setupButtonProtection();

    // 延迟再次确保按钮状态正确
    setTimeout(() => {
      this.ensureButtonsActive();
    }, 500);

    // 调试面板已隐藏
    // this.addDebugInfo();
  }

  // 添加屏幕调试信息
  addDebugInfo() {
    // 移除已存在的调试信息
    const existingDebug = this.querySelector('.carousel-debug-info');
    if (existingDebug) {
      existingDebug.remove();
    }

    const motionList = this.querySelector('motion-list');
    const cards = motionList ? motionList.querySelectorAll('.video-carousel-card') : [];
    
    const debugInfo = document.createElement('div');
    debugInfo.className = 'carousel-debug-info';
    debugInfo.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 10px;
      border-radius: 5px;
      font-family: monospace;
      font-size: 12px;
      z-index: 9999;
      max-width: 300px;
    `;

    this.updateDebugInfo(debugInfo, motionList, cards);
    document.body.appendChild(debugInfo);

    // 每秒更新调试信息
    this.debugInterval = setInterval(() => {
      this.updateDebugInfo(debugInfo, motionList, cards);
    }, 1000);
  }

  // 更新调试信息
  updateDebugInfo(debugElement, motionList, cards) {
    if (!motionList) {
      debugElement.innerHTML = '<div style="color: red;">❌ Motion-list not found</div>';
      return;
    }

    const scrollInfo = {
      cardsCount: cards.length,
      scrollLeft: Math.round(motionList.scrollLeft),
      scrollWidth: motionList.scrollWidth,
      clientWidth: motionList.clientWidth,
      canScroll: motionList.scrollWidth > motionList.clientWidth,
      maxScroll: motionList.scrollWidth - motionList.clientWidth
    };

    // 获取屏幕和窗口信息
    const screenInfo = {
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1
    };

    // 获取自适应计算信息
    const adaptiveInfo = this.cardDimensions || this.calculateCardDimensions();

    // 获取容器宽度信息
    const containerInfo = {
      offsetWidth: motionList.offsetWidth,
      clientWidth: motionList.clientWidth,
      scrollWidth: motionList.scrollWidth,
      boundingWidth: Math.round(motionList.getBoundingClientRect().width)
    };

    debugElement.innerHTML = `
      <div style="color: #4CAF50; font-weight: bold;">🎬 Video Carousel Debug</div>
      <div style="border-bottom: 1px solid #666; margin: 5px 0; padding-bottom: 3px;">
        <div style="color: #FFA500; font-weight: bold;">📺 Screen Info</div>
        <div>🖥️ Screen: <span style="color: #87CEEB;">${screenInfo.screenWidth} × ${screenInfo.screenHeight}px</span></div>
        <div>🌐 Window: <span style="color: #98FB98;">${screenInfo.windowWidth} × ${screenInfo.windowHeight}px</span></div>
        <div>🔍 DPR: <span style="color: #DDA0DD;">${screenInfo.devicePixelRatio}x</span></div>
      </div>
      <div style="border-bottom: 1px solid #666; margin: 5px 0; padding-bottom: 3px;">
        <div style="color: #87CEEB; font-weight: bold;">📦 Container Dimensions</div>
        <div>📐 Offset Width: <span style="color: #FFD700;">${containerInfo.offsetWidth}px</span></div>
        <div>👁️ Client Width: <span style="color: #98FB98;">${containerInfo.clientWidth}px</span></div>
        <div>📏 Scroll Width: <span style="color: #FFA07A;">${containerInfo.scrollWidth}px</span></div>
        <div>📊 Bounding Width: <span style="color: #DDA0DD;">${containerInfo.boundingWidth}px</span></div>
      </div>
      <div style="border-bottom: 1px solid #666; margin: 5px 0; padding-bottom: 3px;">
        <div style="color: #FFB6C1; font-weight: bold;">🔢 Screen-Based Sizing</div>
        <div>🖥️ Base Width: <span style="color: #87CEEB;">${adaptiveInfo.screenWidth}px</span></div>
        <div>📦 Calc Width: <span style="color: #FFB6C1;">${adaptiveInfo.containerWidth}px</span></div>
        <div>📏 Card Width: <span style="color: #FFA07A;">${adaptiveInfo.cardWidth}px</span></div>
        <div>📐 Gap: <span style="color: #20B2AA;">${adaptiveInfo.gap}px</span> (User Setting: ${adaptiveInfo.userGap}px)</div>
        <div>👁️ Visible Cards: <span style="color: #9370DB;">${adaptiveInfo.visibleCards}</span></div>
        <div>🔢 Scroll Step: <span style="color: #32CD32;">${adaptiveInfo.scrollAmount}px</span></div>
      </div>
      <div>📱 Total Cards: <span style="color: yellow;">${scrollInfo.cardsCount}</span></div>
      <div>📏 Current Scroll: <span style="color: cyan;">${scrollInfo.scrollLeft}px</span></div>
      <div>🎯 Max Scroll: <span style="color: pink;">${scrollInfo.maxScroll}px</span></div>
      <div>✅ Can Scroll: <span style="color: ${scrollInfo.canScroll ? 'green' : 'red'};">${scrollInfo.canScroll ? 'YES' : 'NO'}</span></div>
      <div style="margin-top: 5px; font-size: 10px; color: #ccc;">Last Update: ${new Date().toLocaleTimeString()}</div>
    `;
  }

  // 计算基于用户设置的卡片尺寸
  calculateCardDimensions() {
    const motionList = this.querySelector('motion-list');
    if (!motionList) return { cardWidth: 320, gap: 16, containerWidth: window.innerWidth };

    // 获取实际可用宽度 - 考虑section的max-width限制
    const section = this.closest('section') || this;
    const sectionRect = section.getBoundingClientRect();
    const actualAvailableWidth = sectionRect.width || window.innerWidth;
    
    // 使用实际可用宽度而不是屏幕宽度
    const screenWidth = Math.min(actualAvailableWidth, window.innerWidth);
    const cards = motionList.querySelectorAll('.video-carousel-card');
    const cardCount = cards.length;
    const isMobile = screenWidth < 768;
    
    // 读取用户设置的桌面端列数
    const desktopColumns = parseInt(this.getAttribute('data-desktop-columns')) || 3;
    
    // 读取用户设置的Card Gap
    const motionListStyle = window.getComputedStyle(motionList);
    
    // 首先尝试读取CSS变量 --card-gap
    let userGap = 16; // 默认值
    const cardGapVariable = motionListStyle.getPropertyValue('--card-gap');
    
    if (cardGapVariable) {
      // 解析CSS变量值（通常是px单位）
      const gapMatch = cardGapVariable.trim().match(/(\d+(?:\.\d+)?)/);
      if (gapMatch) {
        userGap = parseFloat(gapMatch[1]);
      }
    } else {
      // 如果CSS变量不存在，回退到gap属性
      const cssGap = motionListStyle.getPropertyValue('gap');
      if (cssGap) {
        const gapMatch = cssGap.match(/(\d+(?:\.\d+)?)/);
        if (gapMatch) {
          userGap = parseFloat(gapMatch[1]);
          // 如果是rem单位，转换为px（假设1rem = 16px）
          if (cssGap.includes('rem')) {
            userGap = userGap * 16;
          }
        }
      }
    }
    
    // 移动端始终显示1列，桌面端使用用户设置
    let targetColumns;
    if (isMobile) {
      targetColumns = 1; // 移动端固定1列
    } else {
      targetColumns = desktopColumns;
    }
    
    // 确保不超过实际卡片数量
    targetColumns = Math.min(targetColumns, cardCount);
    
    // 基础参数
    const actualGap = userGap;     // 使用用户设置的间距
    
    // 动态计算容器的实际可用宽度，考虑所有边距和padding
    const container = this.querySelector('.video-carousel-container');
    const pageWidth = this.querySelector('.page-width');
    let containerPadding = 0;
    
    if (container) {
      const containerStyles = window.getComputedStyle(container);
      const marginLeft = parseFloat(containerStyles.marginLeft) || 0;
      const marginRight = parseFloat(containerStyles.marginRight) || 0;
      const paddingLeft = parseFloat(containerStyles.paddingLeft) || 0;
      const paddingRight = parseFloat(containerStyles.paddingRight) || 0;
      containerPadding = marginLeft + marginRight + paddingLeft + paddingRight;
    }
    
    if (pageWidth && containerPadding === 0) {
      const pageWidthStyles = window.getComputedStyle(pageWidth);
      const paddingLeft = parseFloat(pageWidthStyles.paddingLeft) || 0;
      const paddingRight = parseFloat(pageWidthStyles.paddingRight) || 0;
      containerPadding = paddingLeft + paddingRight;
    }
    
    // 如果没有检测到任何padding/margin，使用默认值
    if (containerPadding === 0) {
      containerPadding = 40; // 默认预留值
    }

    // 计算可用的实际宽度（屏幕宽度减去实际的内边距）
    const availableWidth = screenWidth - containerPadding;

    // 计算卡片宽度 = (可用宽度 - 间距总宽度) / 目标列数
    const totalGapWidth = (targetColumns - 1) * actualGap;
    let calculatedCardWidth = (availableWidth - totalGapWidth) / targetColumns;
    
    // 为移动端和桌面端设置不同的宽度限制
    let minCardWidth, maxCardWidth;
    if (isMobile) {
      minCardWidth = 200;      // 移动端最小宽度
      maxCardWidth = screenWidth - 60; // 移动端最大宽度（留出边距）
    } else {
      minCardWidth = 180;      // 桌面端最小宽度
      maxCardWidth = 600;      // 桌面端最大宽度
    }
    
    // 限制卡片宽度在合理范围内
    calculatedCardWidth = Math.max(minCardWidth, Math.min(maxCardWidth, calculatedCardWidth));
    
    const finalCardWidth = Math.floor(calculatedCardWidth);
    
    // 计算实际需要的容器宽度
    const actualContainerWidth = finalCardWidth * targetColumns + totalGapWidth;
    
    return { 
      cardWidth: finalCardWidth, 
      gap: actualGap,
      visibleCards: targetColumns,
      scrollAmount: finalCardWidth + actualGap,
      containerWidth: actualContainerWidth,
      screenWidth: screenWidth,
      desktopColumns: desktopColumns,
      mobileColumns: 1, // 移动端固定1列
      isMobile: isMobile,
      cardCount: cardCount,
      userGap: userGap // 原始用户设置的gap值
    };
  }

  // 强制应用轮播样式（基于屏幕宽度）- 修复：保留用户margin设置
  forceCarouselStyles() {
    const motionList = this.querySelector('motion-list');
    if (!motionList) return;

    // 计算基于屏幕的自适应尺寸
    const dimensions = this.calculateCardDimensions();

    // 确保所有父容器都允许滚动和完全靠左对齐
    const slider = this.querySelector('slider-element');
    const container = this.querySelector('.video-carousel-container');
    const pageWidth = this.querySelector('.page-width');
    const grid = this.closest('.grid');
    const section = this.closest('section');

    // 获取用户设置的边距（从CSS变量）
    let userMarginLeft = '0px', userMarginRight = '0px', userContainerWidth = '100%';
    if (section) {
      const computedSection = window.getComputedStyle(section);
      userMarginLeft = computedSection.getPropertyValue('--section-margin-left').trim() || '0px';
      userMarginRight = computedSection.getPropertyValue('--section-margin-right').trim() || '0px';
      
      // 计算容器宽度（考虑用户边距）
      const leftPx = parseFloat(userMarginLeft) || 0;
      const rightPx = parseFloat(userMarginRight) || 0;
      if (leftPx > 0 || rightPx > 0) {
        userContainerWidth = `calc(100% - ${leftPx + rightPx}px)`;
      }
    }

    // 清除section和grid的间距（但保留用户设置的section margin），禁用垂直滚动
    if (section) {
      section.style.cssText += `
        padding-left: 0 !important;
        overflow-x: hidden !important;
        overflow-y: hidden !important;
        height: auto !important;
        min-height: auto !important;
        max-height: none !important;
      `;
      // 不再强制设置 margin-left: 0，保留CSS设置
    }

    if (grid) {
      grid.style.cssText += `
        margin-left: 0 !important;
        padding-left: 0 !important;
        margin-right: 0 !important;
        padding-right: 0 !important;
      `;
    }

    if (slider) {
      slider.style.cssText += `
        overflow-x: hidden !important;
        overflow-y: hidden !important;
        width: 100% !important;
        max-width: 100% !important;
        height: auto !important;
        min-height: auto !important;
        max-height: none !important;
        margin: 0 !important;
        padding: 0 !important;
        text-align: left !important;
        position: relative !important;
        left: 0 !important;
      `;
    }

    // 关键修复：保留用户设置的container margin，禁用垂直滚动
    if (container) {
      container.style.cssText += `
        overflow-x: hidden !important;
        overflow-y: hidden !important;
        width: ${userContainerWidth} !important;
        max-width: ${userContainerWidth} !important;
        height: auto !important;
        min-height: auto !important;
        max-height: none !important;
        margin-left: ${userMarginLeft} !important;
        margin-right: ${userMarginRight} !important;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        padding: 0 !important;
        text-align: left !important;
        position: relative !important;
        left: 0 !important;
        box-sizing: border-box !important;
      `;
    }

    if (pageWidth) {
      pageWidth.style.cssText += `
        overflow-x: hidden !important;
        overflow-y: hidden !important;
        width: 100% !important;
        max-width: 100% !important;
        height: auto !important;
        min-height: auto !important;
        max-height: none !important;
        margin: 0 !important;
        padding: 0 !important;
        text-align: left !important;
        position: relative !important;
        left: 0 !important;
      `;
    }

    // 设置motion-list为精确的计算宽度，并完全贴近左边
    // 确保容器宽度不超过父容器的实际宽度
    const parentContainer = container || this.querySelector('.video-carousel-container');
    const parentRect = parentContainer ? parentContainer.getBoundingClientRect() : null;
    const maxAllowedWidth = parentRect ? parentRect.width : dimensions.containerWidth;
    const finalContainerWidth = Math.min(dimensions.containerWidth, maxAllowedWidth);
    
    motionList.style.cssText = `
      display: flex !important;
      flex-wrap: nowrap !important;
      overflow-x: scroll !important;
      overflow-y: hidden !important;
      scroll-behavior: smooth !important;
      scroll-snap-type: x mandatory !important;
      gap: ${dimensions.gap}px !important;
      width: ${finalContainerWidth}px !important;
      max-width: ${finalContainerWidth}px !important;
      margin: 0 !important;
      margin-left: 0 !important;
      padding: 0 !important;
      padding-left: 0 !important;
      grid-template-columns: none !important;
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;
      position: relative !important;
      transform: none !important;
      box-sizing: border-box !important;
      left: 0 !important;
      will-change: scroll-position !important;
    `;
    
    const cards = motionList.querySelectorAll('.video-carousel-card');
    cards.forEach(card => {
      card.style.cssText = `
        flex: 0 0 ${dimensions.cardWidth}px !important;
        width: ${dimensions.cardWidth}px !important;
        min-width: ${dimensions.cardWidth}px !important;
        max-width: ${dimensions.cardWidth}px !important;
        display: block !important;
        position: relative !important;
        transform: none !important;
        box-sizing: border-box !important;
        scroll-snap-align: start !important;
      `;
    });
    
    // 添加额外的滚动条隐藏样式
    const motionListStyle = document.createElement('style');
    motionListStyle.textContent = `
      video-carousel motion-list::-webkit-scrollbar {
        display: none !important;
      }
    `;
    if (!document.head.querySelector('style[data-hide-scrollbar]')) {
      motionListStyle.setAttribute('data-hide-scrollbar', 'true');
      document.head.appendChild(motionListStyle);
    }
    
    // 存储计算结果供其他方法使用
    this.cardDimensions = dimensions;
    
    // 样式应用完成
  }

  // 强制显示按钮
  forceShowButtons() {
    if (window.innerWidth < 768) return;

    const allButtons = this.querySelectorAll('button[is="previous-button"], button[is="next-button"]');
    allButtons.forEach(button => {
      button.style.display = 'block';
      button.style.visibility = 'visible';
      button.style.opacity = '1';
      button.hidden = false;
    });

    const indicators = this.querySelectorAll('.indicators');
    indicators.forEach(indicator => {
      indicator.style.display = 'flex';
      indicator.style.visibility = 'visible';
      indicator.style.opacity = '1';
    });
  }

  // 设置轮播按钮
  setupCarouselButtons() {
    const motionList = this.querySelector('motion-list');
    if (!motionList) return;

    // 查找按钮的多种方式
    let prevButtons = this.querySelectorAll('button[is="previous-button"]');
    let nextButtons = this.querySelectorAll('button[is="next-button"]');
    
    // 如果找不到，尝试其他选择器
    if (prevButtons.length === 0) {
      prevButtons = this.querySelectorAll('.slider__button--prev, [data-direction="previous"], .carousel-prev');
    }
    
    if (nextButtons.length === 0) {
      nextButtons = this.querySelectorAll('.slider__button--next, [data-direction="next"], .carousel-next');
    }

    prevButtons.forEach((button, index) => {
      // 移除disabled状态
      button.disabled = false;
      button.removeAttribute('disabled');
      
      // 强制移除所有现有的事件监听器
      const newButton = button.cloneNode(true);
      // 确保克隆的按钮没有disabled状态
      newButton.disabled = false;
      newButton.removeAttribute('disabled');
      
      if (button.parentNode) {
        button.parentNode.replaceChild(newButton, button);
      }
      
      // 强制确保按钮可见和可点击
      newButton.style.cssText += `
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        position: relative !important;
        z-index: 999 !important;
      `;
      newButton.disabled = false;
      newButton.hidden = false;
      
      // 添加事件监听器
      const clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // 重新获取motion-list确保是最新的引用
        const currentMotionList = this.querySelector('motion-list');
        if (currentMotionList) {
          this.scrollCarousel(currentMotionList, 'previous');
        }
      };
      
      // 添加多种事件类型
      newButton.addEventListener('click', clickHandler, { capture: true });
      newButton.addEventListener('mousedown', clickHandler, { capture: true });
      newButton.addEventListener('touchstart', clickHandler, { capture: true });
    });

    nextButtons.forEach((button, index) => {
      // 移除disabled状态
      button.disabled = false;
      button.removeAttribute('disabled');
      
      // 强制移除所有现有的事件监听器
      const newButton = button.cloneNode(true);
      if (button.parentNode) {
        button.parentNode.replaceChild(newButton, button);
      }
      
      // 强制确保按钮可见和可点击
      newButton.style.cssText += `
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        position: relative !important;
        z-index: 999 !important;
      `;
      newButton.disabled = false;
      newButton.hidden = false;
      
      // 添加事件监听器
      const clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // 重新获取motion-list确保是最新的引用
        const currentMotionList = this.querySelector('motion-list');
        if (currentMotionList) {
          this.scrollCarousel(currentMotionList, 'next');
        }
      };
      
      // 添加多种事件类型
      newButton.addEventListener('click', clickHandler, { capture: true });
      newButton.addEventListener('mousedown', clickHandler, { capture: true });
      newButton.addEventListener('touchstart', clickHandler, { capture: true });
    });

    // 延迟强制显示按钮
    setTimeout(() => {
      this.forceShowButtons();
    }, 100);
  }

  // 轮播滚动 - 优化后的丝滑版本
  scrollCarousel(motionList, direction) {
    // 防抖机制：防止快速连续点击
    const now = Date.now();
    if (this.lastScrollTime && now - this.lastScrollTime < 300) {
      return; // 300ms内不允许重复滚动
    }
    this.lastScrollTime = now;

    // 获取自适应计算的尺寸，如果没有则重新计算
    if (!this.cardDimensions) {
      this.cardDimensions = this.calculateCardDimensions();
    }
    
    const scrollAmount = this.cardDimensions.scrollAmount;
    const currentScroll = motionList.scrollLeft;
    const scrollWidth = motionList.scrollWidth;
    const clientWidth = motionList.clientWidth;
    const maxScroll = scrollWidth - clientWidth;
    
    // 计算目标滚动位置，支持循环滚动
    let newScroll;
    
    // 检查是否到达边界，实现循环滚动
    if (direction === 'next' && currentScroll >= maxScroll - 5) {
      // 到达右边界时，滚动回开头
      newScroll = 0;
    } else if (direction === 'previous' && currentScroll <= 5) {
      // 到达左边界时，滚动到最后
      newScroll = maxScroll;
    } else {
      // 正常滚动
      if (direction === 'next') {
        newScroll = Math.min(currentScroll + scrollAmount, maxScroll);
      } else {
        newScroll = Math.max(currentScroll - scrollAmount, 0);
      }
    }

    // 确保滚动行为是smooth
    motionList.style.scrollBehavior = 'smooth';
    
    // 使用统一的scrollTo方法，最丝滑的方式
    motionList.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  }

  // 强制在桌面端显示轮播按钮
  forceShowButtons(event) {
    if (window.innerWidth >= 768) {
      // 桌面端：强制显示所有轮播按钮
      this.forceShowButtonsOnDesktop();
    }
  }

  // 设置按钮保护机制，防止被外部代码禁用
  setupButtonProtection() {
    if (window.innerWidth < 768) return;

    const prevButtons = this.querySelectorAll('button[is="previous-button"]');
    const nextButtons = this.querySelectorAll('button[is="next-button"]');
    
    [...prevButtons, ...nextButtons].forEach(button => {
      // 重写 disabled 属性，防止被设置为 true
      Object.defineProperty(button, 'disabled', {
        get: function() { return false; },
        set: function(value) { 
          // 忽略所有试图禁用按钮的操作
          console.log('🛡️ Prevented button disable attempt');
        },
        configurable: true
      });

      // 重写 setAttribute，防止通过 setAttribute 禁用
      const originalSetAttribute = button.setAttribute;
      button.setAttribute = function(name, value) {
        if (name === 'disabled') {
          console.log('🛡️ Prevented disabled attribute setting');
          return;
        }
        originalSetAttribute.call(this, name, value);
      };

      // 重写 removeAttribute 的行为
      const originalRemoveAttribute = button.removeAttribute;
      button.removeAttribute = function(name) {
        originalRemoveAttribute.call(this, name);
        if (name === 'disabled') {
          this.disabled = false; // 确保始终保持启用状态
        }
      };
    });

    // 定期检查和修复按钮状态
    if (this.buttonProtectionInterval) {
      clearInterval(this.buttonProtectionInterval);
    }
    
    this.buttonProtectionInterval = setInterval(() => {
      this.ensureButtonsActive();
    }, 1000);
  }

  // 确保按钮始终处于可点击状态
  ensureButtonsActive() {
    if (window.innerWidth < 768) return;

    const prevButtons = this.querySelectorAll('button[is="previous-button"]');
    const nextButtons = this.querySelectorAll('button[is="next-button"]');
    
    [...prevButtons, ...nextButtons].forEach(button => {
      // 强制移除 disabled 属性
      button.removeAttribute('disabled');
      
      // 强制设置样式
      button.style.cssText += `
        pointer-events: auto !important;
        opacity: 1 !important;
        display: block !important;
        visibility: visible !important;
      `;
      
      button.hidden = false;
      
      // 确保有点击事件监听器
      if (!button.dataset.hasCustomListener) {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const motionList = this.querySelector('motion-list');
          if (motionList) {
            const isNext = button.getAttribute('is') === 'next-button';
            this.scrollCarousel(motionList, isNext ? 'next' : 'previous');
          }
        }, { capture: true });
        
        button.dataset.hasCustomListener = 'true';
      }
    });
  }

  // 强制在桌面端显示按钮的方法
  forceShowButtonsOnDesktop() {
    if (window.innerWidth >= 768) {
      const prevButtons = this.querySelectorAll('button[is="previous-button"]');
      const nextButtons = this.querySelectorAll('button[is="next-button"]');
      
      prevButtons.forEach(button => {
        button.style.display = 'block';
        button.style.visibility = 'visible';
        button.style.opacity = '1';
        button.hidden = false;
        // 不强制设置disabled状态，让SliderElement管理
      });
      
      nextButtons.forEach(button => {
        button.style.display = 'block';
        button.style.visibility = 'visible';
        button.style.opacity = '1';
        button.hidden = false;
        // 不强制设置disabled状态，让SliderElement管理
      });

      // 设置MutationObserver监听按钮状态变化
      if (!this.buttonObserver) {
        this.buttonObserver = new MutationObserver((mutations) => {
          if (window.innerWidth >= 768) {
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && 
                  (mutation.attributeName === 'hidden' || 
                   mutation.attributeName === 'style')) {
                const button = mutation.target;
                if (button.tagName === 'BUTTON' && 
                    (button.hasAttribute('is') && 
                     (button.getAttribute('is') === 'previous-button' || 
                      button.getAttribute('is') === 'next-button'))) {
                  // 强制重新显示按钮，但不改变disabled状态
                  setTimeout(() => {
                    button.style.display = 'block';
                    button.style.visibility = 'visible';
                    button.style.opacity = '1';
                    button.hidden = false;
                  }, 10);
                }
              }
            });
          }
        });

        // 开始观察按钮的属性变化，但不监听disabled属性
        [...prevButtons, ...nextButtons].forEach(button => {
          this.buttonObserver.observe(button, { 
            attributes: true, 
            attributeFilter: ['hidden', 'style', 'class'] 
          });
        });
      }
    }
  }

  // 设置自定义轮播逻辑（桌面端）
  setupCustomCarouselLogic(slider) {
    const motionList = slider.querySelector('motion-list');
    if (!motionList) return;

    const prevButtons = this.querySelectorAll('button[is="previous-button"]');
    const nextButtons = this.querySelectorAll('button[is="next-button"]');

    // 阻止原始的 SliderElement 事件，添加自定义逻辑
    prevButtons.forEach((button, index) => {
      // 移除所有现有的点击事件监听器
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      
      // 添加自定义点击事件
      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.scrollToPreviousCard(motionList);
      }, true);
    });

    nextButtons.forEach((button, index) => {
      // 移除所有现有的点击事件监听器
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      
      // 添加自定义点击事件
      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.scrollToNextCard(motionList);
      }, true);
    });

    // 确保按钮仍然可见
    setTimeout(() => {
      this.forceShowButtonsOnDesktop();
    }, 100);
  }

  // 滚动到上一张卡片 - 丝滑版本
  scrollToPreviousCard(motionList) {
    this.scrollCarousel(motionList, 'previous');
  }

  // 滚动到下一张卡片 - 丝滑版本
  scrollToNextCard(motionList) {
    this.scrollCarousel(motionList, 'next');
  }

  handleCardClick(event) {
    const playButton = event.target.closest('.play-button');
    const card = event.target.closest('.video-carousel-card');
    const videoThumbnail = event.target.closest('.video-thumbnail');
    
    if (card) {
      event.preventDefault();
      event.stopPropagation();
      
      // 如果点击的是播放按钮，播放视频
      if (playButton) {
        const videoUrl = playButton.dataset.videoUrl;
        const videoId = playButton.dataset.video;
        const videoSources = playButton.dataset.videoSources;
        
        // 已移除调试信息
        
        if (videoUrl && this.isExternalVideo(videoUrl)) {
          // YouTube/Vimeo 外部视频
          this.playExternalVideo(card, videoUrl);
        } else if (videoSources || videoId) {
          // Shopify 上传的视频
          this.playShopifyVideo(card, videoSources || videoId);
        } else {
          console.warn('No valid video source found');
        }
      } 
      // 如果点击的是正在播放的视频区域，重置为缩略图
      else if (videoThumbnail && card.classList.contains('video-playing')) {
        this.resetVideoToThumbnail(card);
      }
    }
  }

  resetVideoToThumbnail(card) {
    // 移除播放状态
    card.classList.remove('video-playing');
    
    // 恢复原始缩略图
    const thumbnailContainer = card.querySelector('.video-thumbnail');
    if (thumbnailContainer && card.dataset.originalThumbnail) {
      thumbnailContainer.innerHTML = card.dataset.originalThumbnail;
    }
  }

  isExternalVideo(url) {
    return url.includes('youtube.com') || 
           url.includes('youtu.be') || 
           url.includes('vimeo.com');
  }

  playExternalVideo(card, videoUrl) {
    // 获取视频缩略图容器
    const thumbnailContainer = card.querySelector('.video-thumbnail');
    if (!thumbnailContainer) return;

    // 保存原始内容
    if (!card.dataset.originalThumbnail) {
      card.dataset.originalThumbnail = thumbnailContainer.innerHTML;
    }

    let embedUrl = '';
    
    // 检测YouTube视频
    if (videoUrl.includes('youtube.com/watch?v=')) {
      const videoId = videoUrl.split('v=')[1].split('&')[0];
      embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
    } else if (videoUrl.includes('youtu.be/')) {
      const videoId = videoUrl.split('youtu.be/')[1].split('?')[0];
      embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
    }
    // 检测Vimeo视频
    else if (videoUrl.includes('vimeo.com/')) {
      const videoId = videoUrl.split('vimeo.com/')[1].split('?')[0];
      embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
    }

    if (!embedUrl) {
      console.warn('Unsupported video URL:', videoUrl);
      return;
    }

    // 创建iframe
    const iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.frameBorder = '0';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
    `;

    // 替换缩略图内容为iframe
    thumbnailContainer.innerHTML = '';
    thumbnailContainer.appendChild(iframe);
    
    // 添加播放状态类
    card.classList.add('video-playing');
  }

  playShopifyVideo(card, videoSourcesOrId) {
    // 获取视频缩略图容器
    const thumbnailContainer = card.querySelector('.video-thumbnail');
    if (!thumbnailContainer) return;

    // 保存原始内容
    if (!card.dataset.originalThumbnail) {
      card.dataset.originalThumbnail = thumbnailContainer.innerHTML;
    }

    // 创建HTML5 video元素
    const video = document.createElement('video');
    video.controls = true;
    video.autoplay = true;
    video.muted = this.getAttribute('data-enable-video-muted') === 'true';
    video.loop = this.getAttribute('data-enable-video-looping') === 'true';
    video.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border: none;
    `;

    // 解析视频源
    let videoSources = [];
    
    if (videoSourcesOrId && videoSourcesOrId.includes('|')) {
      // 如果包含|，说明是多个URL
      videoSources = videoSourcesOrId.split('|').filter(url => url.trim());
    } else if (videoSourcesOrId && videoSourcesOrId.startsWith('http')) {
      // 单个完整URL
      videoSources = [videoSourcesOrId];
    } else if (videoSourcesOrId) {
      // 视频ID，需要构建URL
      this.tryAlternativeVideoFormats(video, videoSourcesOrId);
      thumbnailContainer.innerHTML = '';
      thumbnailContainer.appendChild(video);
      card.classList.add('video-playing');
      return;
    }

    if (videoSources.length === 0) {
      console.error('No valid video sources found');
      this.showVideoError(thumbnailContainer);
      return;
    }

    // 尝试加载第一个视频源
    this.loadVideoWithSources(video, videoSources, 0, thumbnailContainer);
    
    // 替换缩略图内容为video
    thumbnailContainer.innerHTML = '';
    thumbnailContainer.appendChild(video);
    
    // 添加播放状态类
    card.classList.add('video-playing');
  }

  loadVideoWithSources(video, sources, currentIndex, container) {
    if (currentIndex >= sources.length) {
      this.showVideoError(container);
      return;
    }

    const currentSource = sources[currentIndex];
    
    // 移除之前的错误监听器
    video.removeEventListener('error', video._errorHandler);
    video.removeEventListener('loadeddata', video._loadedHandler);
    
    // 添加新的错误处理器
    video._errorHandler = (e) => {
      this.loadVideoWithSources(video, sources, currentIndex + 1, container);
    };
    
    // 添加成功加载处理器
    video._loadedHandler = () => {
      // 视频加载成功
    };
    
    video.addEventListener('error', video._errorHandler, { once: true });
    video.addEventListener('loadeddata', video._loadedHandler, { once: true });
    
    // 设置视频源
    video.src = currentSource;
  }

  tryAlternativeVideoFormats(videoElement, videoId) {
    
    // 尝试不同的Shopify视频URL格式
    const formats = [
      `https://cdn.shopify.com/videos/${videoId}`,
      `https://cdn.shopify.com/s/files/1/videos/${videoId}.mp4`,
      `https://cdn.shopify.com/s/files/1/videos/${videoId}.webm`,
      `https://cdn.shopify.com/s/files/1/videos/${videoId}/video.mp4`,
      `https://cdn.shopify.com/videos/c/o/v/e/r/${videoId}`,
      // 尝试不同的文件扩展名
      `https://cdn.shopify.com/videos/${videoId}.mp4`,
      `https://cdn.shopify.com/videos/${videoId}.webm`
    ];

    this.loadVideoWithSources(videoElement, formats, 0, videoElement.parentNode);
  }

  showVideoError(container) {
    // 显示错误消息
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      background: rgba(0,0,0,0.8);
      padding: 15px;
      border-radius: 8px;
      text-align: center;
      font-size: 14px;
      max-width: 200px;
      z-index: 10;
    `;
    errorDiv.innerHTML = `
      <div style="margin-bottom: 8px;">⚠️</div>
      <div>Video unavailable</div>
      <div style="font-size: 12px; opacity: 0.8; margin-top: 4px;">Check console for details</div>
    `;
    container.appendChild(errorDiv);
  }















  onSliderChange(event) {
    // 当滑块切换时，暂停所有正在播放的视频
    this.pauseAllPlayingVideos();
  }

  pauseAllPlayingVideos() {
    // 查找所有正在播放的视频
    const playingCards = this.querySelectorAll('.video-playing');
    playingCards.forEach(card => {
      const iframe = card.querySelector('iframe');
      const video = card.querySelector('video');
      
      if (iframe) {
        // 通过重置src来停止iframe视频播放
        const src = iframe.src;
        iframe.src = '';
        iframe.src = src.replace('autoplay=1', 'autoplay=0');
      } else if (video) {
        // 暂停HTML5视频
        video.pause();
      }
      
      // 移除播放状态类
      card.classList.remove('video-playing');
    });
  }

  // 初始化移动端Swiper
  initMobileSwiper() {
    // 只在移动端初始化Swiper
    if (window.innerWidth >= 768) return;
    
    // 检查Swiper是否已加载
    if (typeof Swiper === 'undefined') {
      // 如果Swiper未加载，延迟重试
      setTimeout(() => {
        this.initMobileSwiper();
      }, 500);
      return;
    }

    const sectionId = this.closest('section')?.id || '';
    const realSectionId = sectionId.replace('shopify-section-', '');
    let finalSwiperContainer = this.querySelector(`#MobileSwiper-${realSectionId}`);
    
    // 如果按ID找不到，尝试直接通过类名查找
    if (!finalSwiperContainer) {
      finalSwiperContainer = this.querySelector('.mobile-carousel.swiper');
    }
    
    if (!finalSwiperContainer) {
      return;
    }

    // 销毁已存在的Swiper实例
    if (this.mobileSwiper) {
      this.mobileSwiper.destroy(true, true);
      this.mobileSwiper = null;
    }

    try {
      // 初始化Swiper
      this.mobileSwiper = new Swiper(finalSwiperContainer, {
        // 基本配置
        slidesPerView: 1,
        spaceBetween: 0,
        centeredSlides: true,
        autoHeight: true, // 自适应高度，避免上下滚动条
        watchOverflow: true, // 当slides不足时禁用swiper
        
        // 触摸配置
        touchRatio: 1,
        touchAngle: 45,
        grabCursor: true,
        allowTouchMove: true,
        simulateTouch: true,
        passiveListeners: false,
        
        // 分页
        pagination: {
          el: finalSwiperContainer.querySelector('.swiper-pagination'),
          clickable: true,
          dynamicBullets: false
        },
        
        // 性能优化
        speed: 300,
        preloadImages: false,
        lazy: {
          loadPrevNext: true,
        },
        
        // 响应式breakpoints - 移动端统一配置
        breakpoints: {
          320: {
            slidesPerView: 1,
            spaceBetween: 8,
            centeredSlides: true,
            autoHeight: true
          },
          480: {
            slidesPerView: 1,
            spaceBetween: 8,
            centeredSlides: true,
            autoHeight: true
          },
          1023: {
            slidesPerView: 1,
            spaceBetween: 8,
            centeredSlides: true,
            autoHeight: true
          }
        },

        // 事件监听
        on: {
          slideChange: () => {
            // 当滑动时暂停所有视频
            this.pauseAllPlayingVideos();
          }
        }
      });

      // 为移动端Swiper添加视频播放事件
      finalSwiperContainer.addEventListener('click', this.handleCardClick.bind(this));

      // 强制更新Swiper布局，确保正确显示
      setTimeout(() => {
        if (this.mobileSwiper && this.mobileSwiper.update) {
          this.mobileSwiper.update();
          this.mobileSwiper.updateAutoHeight(300); // 300ms动画过渡
        }
      }, 100);

    } catch (error) {
      console.error('Swiper初始化失败:', error);
    }
  }


}

// 注册自定义元素
if (!customElements.get('video-carousel')) {
  customElements.define('video-carousel', VideoCarousel);
}



/*
// 测试函数（开发用）
window.testSimpleScroll = function(pixels = 340) {
  const motionList = document.querySelector('motion-list');
  if (motionList) {
    motionList.scrollLeft += pixels;
  }
};

window.testLeftScroll = function() {
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach(carousel => {
    const motionList = carousel.querySelector('motion-list');
    if (motionList) {
      carousel.scrollCarousel(motionList, 'previous');
    }
  });
};
*/

// 测试功能（生产环境可移除）
/*
window.testLeftButton = function() {
  // 测试左箭头按钮功能
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach(carousel => {
    const prevButtons = carousel.querySelectorAll('button[is="previous-button"]');
    prevButtons.forEach(button => button.click());
  });
};
*/

/*
// 诊断功能（开发调试用）
window.diagnoseButtonBinding = function() {
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach(carousel => {
    carousel.setupCarouselButtons();
  });
};
*/

/*
// 强制重新绑定按钮（维护工具）
window.forceRebindButtons = function() {
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach(carousel => {
    carousel.cardDimensions = null;
    carousel.forceCarouselStyles();
    carousel.setupCarouselButtons();
  });
};
*/

/*
// 测试设置（开发用）
window.setupLeftScrollTest = function() {
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach(carousel => {
    const motionList = carousel.querySelector('motion-list');
    if (motionList) {
      const maxScroll = motionList.scrollWidth - motionList.clientWidth;
      motionList.scrollLeft = Math.floor(maxScroll / 2);
    }
  });
};
*/

/*
// 修复工具（维护用）
window.fixLeftScroll = function() {
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach(carousel => {
    carousel.cardDimensions = null;
    carousel.forceCarouselStyles();
    carousel.setupCarouselButtons();
  });
};
*/

/*
// 工具函数（维护用）
window.forceCarouselStyles = function() {
  const motionList = document.querySelector('motion-list');
  if (motionList) {
    const carousels = document.querySelectorAll('video-carousel');
    carousels.forEach(carousel => {
      carousel.cardDimensions = null;
      carousel.forceCarouselStyles();
    });
  }
};

// 测试Desktop Columns设置
window.testDesktopColumns = function() {
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach((carousel, index) => {
    const dimensions = carousel.calculateCardDimensions();
    console.log(`🎬 Carousel ${index + 1} - Desktop Columns Test:`, {
      '📊 Desktop Columns Setting': dimensions.desktopColumns,
      '👁️ Currently Visible Cards': dimensions.visibleCards,
      '📏 Card Width': dimensions.cardWidth + 'px',
      '➡️ Scroll Amount': dimensions.scrollAmount + 'px',
      '📱 Is Mobile': dimensions.isMobile,
      '🖥️ Screen Width': dimensions.screenWidth + 'px',
      '🎯 Total Cards': dimensions.cardCount
    });
    
    // 验证逻辑是否正确
    const expectedColumns = dimensions.isMobile ? 1 : dimensions.desktopColumns; // 移动端固定1列
    const actualColumns = Math.min(expectedColumns, dimensions.cardCount);
    
    if (dimensions.visibleCards === actualColumns) {
      console.log('✅ Layout calculation is working correctly!');
    } else {
      console.warn('⚠️ Layout calculation mismatch:', {
        expected: actualColumns,
        actual: dimensions.visibleCards
      });
    }
  });
  
  console.log('\n💡 Tip: Change "Desktop Columns" in the Shopify admin to see different desktop layouts!');
  console.log('📱 Note: Mobile is automatically optimized to display 1 column.');
};

// 测试滚动丝滑度（开发用）
window.testSmoothScrolling = function() {
  const carousels = document.querySelectorAll('video-carousel');
  console.log('🚀 Testing smooth scrolling performance...');
  
  carousels.forEach((carousel, index) => {
    const motionList = carousel.querySelector('motion-list');
    if (motionList) {
      console.log(`🎬 Carousel ${index + 1} scroll test:`);
      
      // 测试连续滚动
      setTimeout(() => {
        console.log('➡️ Scrolling right...');
        carousel.scrollCarousel(motionList, 'next');
      }, 500);
      
      setTimeout(() => {
        console.log('➡️ Scrolling right again...');
        carousel.scrollCarousel(motionList, 'next');
      }, 1200);
      
      setTimeout(() => {
        console.log('⬅️ Scrolling left...');
        carousel.scrollCarousel(motionList, 'previous');
      }, 2000);
      
      setTimeout(() => {
        console.log('⬅️ Scrolling left again...');
        carousel.scrollCarousel(motionList, 'previous');
      }, 2800);
      
      setTimeout(() => {
        console.log('✅ Smooth scrolling test completed!');
        console.log('📈 Performance features enabled:');
        console.log('   • CSS scroll-snap for precise alignment');
        console.log('   • Hardware acceleration (will-change)');
        console.log('   • Debouncing for smooth interaction');
        console.log('   • Unified scrollTo API');
      }, 3600);
    }
  });
};

window.debugScrollIssue = function() {
  const motionList = document.querySelector('motion-list');
  if (motionList) {
    return {
      scrollWidth: motionList.scrollWidth,
      clientWidth: motionList.clientWidth,
      canScroll: motionList.scrollWidth > motionList.clientWidth
    };
  }
};
*/

/*
// 更多调试工具（开发用）
window.debugAdaptiveSizing = function() {
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach(carousel => {
    carousel.cardDimensions = null;
    carousel.calculateCardDimensions();
  });
};

window.testAdaptiveScroll = function() {
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach(carousel => {
    const motionList = carousel.querySelector('motion-list');
    if (motionList) {
      carousel.scrollCarousel(motionList, 'next');
    }
  });
};

window.forceAdaptiveStyles = function() {
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach(carousel => {
    carousel.cardDimensions = null;
    carousel.forceCarouselStyles();
  });
};
*/

/*
// 高级调试工具（开发/维护用）
window.testScreenWidthChange = function() {
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach(carousel => {
    if (carousel.resizeHandler) {
      carousel.resizeHandler();
    }
  });
};

window.simulateScreenSizes = function() {
  // 简化版本 - 仅用于快速测试
  return 'Screen size test functionality available';
};

window.debugLeftSpacing = function() {
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach(carousel => {
    const motionList = carousel.querySelector('motion-list');
    if (motionList) {
      const rect = motionList.getBoundingClientRect();
      return Math.round(rect.left);
    }
  });
};
*/

// 额外的进度条样式增强和隐藏滚动条，优化滚动性能
const style = document.createElement('style');
style.textContent = `
  /* 隐藏webkit浏览器的滚动条 */
  video-carousel motion-list::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }
  
  /* 兼容性隐藏和性能优化 */
  video-carousel motion-list {
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
    scroll-behavior: smooth !important;
    will-change: scroll-position !important;
    scroll-snap-type: x mandatory !important;
  }
  
  /* 优化卡片的滚动对齐 */
  video-carousel .video-carousel-card {
    scroll-snap-align: start !important;
    will-change: transform !important;
  }
  
  /* 优化滚动性能 */
  video-carousel * {
    backface-visibility: hidden !important;
    -webkit-backface-visibility: hidden !important;
  }
  
  .video-carousel-video.playing .progress-bar {
    opacity: 1;
  }
  
  .video-carousel-video .progress-bar {
    opacity: 0.7;
    transition: opacity 0.3s ease;
  }
  
  .video-carousel-video:hover .progress-bar {
    opacity: 1;
  }
  
  .progress-bar-fill {
    background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
    box-shadow: 0 0 10px rgba(0, 123, 255, 0.3);
  }
  
  .video-carousel-card:hover .progress-bar-fill {
    box-shadow: 0 0 15px rgba(0, 123, 255, 0.5);
  }
`;

document.head.appendChild(style);

// 测试Card Gap设置
window.testCardGap = function() {
  console.log('🔧 Testing Card Gap Setting...');
  
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach((carousel, index) => {
    console.log(`🎬 Carousel ${index + 1} Card Gap Analysis:`);
    
    const motionList = carousel.querySelector('motion-list');
    if (!motionList) {
      console.error('❌ Motion-list not found');
      return;
    }
    
    const motionListStyle = window.getComputedStyle(motionList);
    
    // 读取CSS变量
    const cardGapVariable = motionListStyle.getPropertyValue('--card-gap');
    console.log(`  🎨 CSS Variable --card-gap: "${cardGapVariable}"`);
    
    // 读取gap属性
    const cssGap = motionListStyle.getPropertyValue('gap');
    console.log(`  📐 CSS Gap Property: "${cssGap}"`);
    
    // 计算结果
    const dimensions = carousel.calculateCardDimensions();
    console.log(`  ✅ Final Used Gap: ${dimensions.gap}px`);
    console.log(`  📊 User Gap Raw: ${dimensions.userGap}px`);
    
    // 验证计算对布局的影响
    console.log(`  🧮 Gap Impact on Layout:`);
    console.log(`    - Total Gap Width: ${(dimensions.visibleCards - 1) * dimensions.gap}px`);
    console.log(`    - Card Width: ${dimensions.cardWidth}px`);
    console.log(`    - Scroll Amount: ${dimensions.scrollAmount}px (Card Width + Gap)`);
    
    // 验证是否正确应用
    const actualGap = parseFloat(cssGap) || 0;
    if (Math.abs(actualGap - dimensions.gap) <= 1) {
      console.log(`  ✅ Gap setting is correctly applied!`);
    } else {
      console.warn(`  ⚠️ Gap mismatch detected:`, {
        expected: dimensions.gap,
        actual: actualGap
      });
    }
  });
  
  console.log('\n💡 Tip: Change "Card Gap" in the Shopify admin to see spacing adjustments!');
  console.log('🎯 Gap affects both visual spacing and scroll calculations.');
};

// 测试移动端卡片宽度计算
window.testMobileCardWidth = function() {
  console.log('📱 Testing Mobile Card Width Calculation...');
  
  if (window.innerWidth >= 768) {
    console.log('⚠️ This test is for mobile screens (width < 768px). Current width:', window.innerWidth + 'px');
    console.log('💡 Resize window to mobile size or open dev tools mobile view to test.');
    return;
  }
  
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach((carousel, index) => {
    console.log(`📱 Carousel ${index + 1} Mobile Width Analysis:`);
    
    const computedStyle = window.getComputedStyle(carousel);
    const leftMargin = parseInt(computedStyle.getPropertyValue('--section-margin-left')) || 0;
    const rightMargin = parseInt(computedStyle.getPropertyValue('--section-margin-right')) || 0;
    const viewportWidth = window.innerWidth;
    
    // JavaScript计算（包含安全边距）
    const safetyBuffer = 4; // 防止溢出的安全边距
    const jsCardWidth = viewportWidth - leftMargin - rightMargin - safetyBuffer;
    
    // CSS计算验证
    const motionList = carousel.querySelector('motion-list');
    const actualCard = motionList ? motionList.querySelector('.video-carousel-card') : null;
    const actualCardWidth = actualCard ? actualCard.offsetWidth : 0;
    
    console.log(`  📊 Calculation Details:`);
    console.log(`    - Screen Width: ${viewportWidth}px`);
    console.log(`    - Left Margin: ${leftMargin}px`);
    console.log(`    - Right Margin: ${rightMargin}px`);
    console.log(`    - Calculated Width: ${jsCardWidth}px (${viewportWidth} - ${leftMargin} - ${rightMargin} - ${safetyBuffer})`);
    console.log(`    - Actual Card Width: ${actualCardWidth}px`);
    
    // 验证计算精度（允许1-2px误差）
    const widthDifference = Math.abs(jsCardWidth - actualCardWidth);
    if (widthDifference <= 2) {
      console.log(`  ✅ Width calculation is accurate! (${widthDifference}px difference)`);
    } else {
      console.warn(`  ⚠️ Width calculation mismatch:`, {
        expected: jsCardWidth,
        actual: actualCardWidth,
        difference: widthDifference
      });
    }
    
    // 测试轮播逻辑
    const cardGap = parseInt(computedStyle.getPropertyValue('--card-gap')) || 8;
    const cardWithGapWidth = jsCardWidth + cardGap;
    console.log(`  🎯 Scroll Logic:`);
    console.log(`    - Card + Gap Width: ${cardWithGapWidth}px (${jsCardWidth} + ${cardGap})`);
    console.log(`    - For 3 cards scroll: ${cardWithGapWidth * 3}px total scroll needed`);
  });
  
  console.log('\n💡 Formula: Card Width = Screen Width - Left Margin - Right Margin - Safety Buffer (4px)');
  console.log('📐 This ensures cards fill available space while preventing page overflow.');
};

// 测试移动端Swiper轮播
window.testMobileSwiper = function() {
  console.log('📱 Testing Mobile Swiper Implementation...');
  
  if (window.innerWidth >= 768) {
    console.log('⚠️ This test is for mobile screens (width < 768px). Current width:', window.innerWidth + 'px');
    console.log('💡 Resize window to mobile size or open dev tools mobile view to test.');
    return;
  }
  
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach((carousel, index) => {
    console.log(`🎬 Carousel ${index + 1} Swiper Analysis:`);
    
    // 检查桌面端轮播是否隐藏
    const desktopCarousel = carousel.querySelector('.desktop-carousel');
    const desktopHidden = desktopCarousel && window.getComputedStyle(desktopCarousel).display === 'none';
    console.log(`  🖥️ Desktop Carousel Hidden: ${desktopHidden ? '✅ YES' : '❌ NO'}`);
    
    // 检查移动端Swiper是否显示
    const mobileCarousel = carousel.querySelector('.mobile-carousel');
    const mobileVisible = mobileCarousel && window.getComputedStyle(mobileCarousel).display !== 'none';
    console.log(`  📱 Mobile Swiper Visible: ${mobileVisible ? '✅ YES' : '❌ NO'}`);
    console.log(`  📱 Mobile Swiper ID: ${mobileCarousel ? mobileCarousel.id : '❌ No ID'}`);
    
    // 检查Swiper实例
    const swiperInstance = carousel.mobileSwiper;
    console.log(`  🎡 Swiper Instance: ${swiperInstance ? '✅ Initialized' : '❌ Not Found'}`);
    
    if (swiperInstance) {
      const slides = mobileCarousel.querySelectorAll('.swiper-slide');
      console.log(`  🎴 Total Slides: ${slides.length}`);
      console.log(`  📍 Active Index: ${swiperInstance.activeIndex}`);
      console.log(`  🎯 Swiper Config:`);
      console.log(`    - Slides Per View: ${swiperInstance.params.slidesPerView}`);
      console.log(`    - Space Between: ${swiperInstance.params.spaceBetween}px`);
      console.log(`    - Speed: ${swiperInstance.params.speed}ms`);
      console.log(`    - Touch Enabled: ${swiperInstance.params.allowTouchMove ? '✅ YES' : '❌ NO'}`);
      console.log(`    - Touch Ratio: ${swiperInstance.params.touchRatio}`);
      
      // 检查分页器
      const pagination = mobileCarousel.querySelector('.swiper-pagination');
      const bullets = pagination ? pagination.querySelectorAll('.swiper-pagination-bullet') : [];
      console.log(`  🔘 Pagination Bullets: ${bullets.length}`);
      
      // 测试滑动功能
      console.log(`  🧪 Testing Swiper Functions:`);
      
      // 测试向右滑动
      if (slides.length > 1) {
        setTimeout(() => {
          console.log('  ➡️ Testing slide to next...');
          swiperInstance.slideNext();
          
          setTimeout(() => {
            console.log(`  📍 New Active Index: ${swiperInstance.activeIndex}`);
            
            // 测试向左滑动
            setTimeout(() => {
              console.log('  ⬅️ Testing slide to previous...');
              swiperInstance.slidePrev();
              
              setTimeout(() => {
                console.log(`  📍 Final Active Index: ${swiperInstance.activeIndex}`);
                console.log('  ✅ Swiper navigation test completed!');
              }, 400);
            }, 1000);
          }, 400);
        }, 500);
      }
    } else {
      // 如果Swiper实例不存在，尝试手动初始化
      console.log('  🔧 Swiper instance not found, attempting manual initialization...');
      carousel.initMobileSwiper();
    }
    
    // 检查视频卡片样式
    const swiperSlides = mobileCarousel ? mobileCarousel.querySelectorAll('.swiper-slide') : [];
    if (swiperSlides.length > 0) {
      const firstSlide = swiperSlides[0];
      const slideWidth = firstSlide.offsetWidth;
      const slideHeight = firstSlide.offsetHeight;
      console.log(`  📏 Slide Dimensions: ${slideWidth} × ${slideHeight}px`);
      
      // 检查视频元素是否存在
      const videoThumbnail = firstSlide.querySelector('.video-thumbnail');
      const playButton = firstSlide.querySelector('.play-button');
      console.log(`  🎬 Video Thumbnail: ${videoThumbnail ? '✅ Found' : '❌ Missing'}`);
      console.log(`  ▶️ Play Button: ${playButton ? '✅ Found' : '❌ Missing'}`);
    }
  });
  
  console.log('\n💡 Tip: Swiper provides better touch experience and eliminates overflow issues on mobile!');
  console.log('🎯 Desktop retains the original implementation while mobile uses Swiper.');
  console.log('📐 Container: left and right padding = 0px, full screen width.');
  console.log('\n🧪 Available Tests:');
  console.log('  - testMobilePadding() - Check container padding & layout');
  console.log('  - checkScrollbars() - Diagnose unwanted scrollbars');
  console.log('  - fixMobileSwiper() - Reinitialize Swiper if needed');
  console.log('  - diagnoseMobileSwiper() - Full diagnostic check');
};

// 快速修复函数
window.fixMobileSwiper = function() {
  console.log('🔧 Attempting to fix mobile Swiper...');
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach((carousel, index) => {
    console.log(`🎬 Fixing Carousel ${index + 1}...`);
    if (carousel.initMobileSwiper) {
      carousel.initMobileSwiper();
    } else {
      console.error('initMobileSwiper method not found');
    }
  });
};

// 移动端Swiper诊断函数
window.diagnoseMobileSwiper = function() {
  console.log('🔍 Diagnosing Mobile Swiper Issues...');
  
  console.log('📱 Screen width:', window.innerWidth);
  console.log('📦 Swiper library loaded:', typeof Swiper !== 'undefined');
  
  const carousels = document.querySelectorAll('video-carousel');
  console.log('🎬 Found carousels:', carousels.length);
  
  carousels.forEach((carousel, index) => {
    console.log(`\n🎬 Carousel ${index + 1}:`);
    console.log('  📍 Element:', carousel);
    
    const section = carousel.closest('section');
    console.log('  🏷️ Section ID:', section?.id);
    
    const mobileCarousel = carousel.querySelector('.mobile-carousel');
    console.log('  📱 Mobile carousel found:', !!mobileCarousel);
    console.log('  📱 Mobile carousel ID:', mobileCarousel?.id);
    console.log('  📱 Mobile carousel visible:', mobileCarousel ? window.getComputedStyle(mobileCarousel).display !== 'none' : false);
    
    const swiperWrapper = mobileCarousel?.querySelector('.swiper-wrapper');
    console.log('  🎡 Swiper wrapper found:', !!swiperWrapper);
    
    const slides = mobileCarousel?.querySelectorAll('.swiper-slide');
    console.log('  🎴 Slides found:', slides?.length || 0);
    
    console.log('  🎡 Swiper instance:', !!carousel.mobileSwiper);
    
    if (carousel.mobileSwiper) {
      console.log('    ✅ Swiper initialized successfully');
      console.log('    📍 Active index:', carousel.mobileSwiper.activeIndex);
      console.log('    🎯 Allow touch move:', carousel.mobileSwiper.params.allowTouchMove);
    } else {
      console.log('    ❌ Swiper not initialized');
    }
  });
  
  console.log('\n💡 If issues persist, try running: fixMobileSwiper()');
};

// 测试桌面端按钮功能 - 增强版
window.testDesktopButtons = function() {
  console.log('🔄 Testing Desktop Carousel Buttons...');
  
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach((carousel, index) => {
    console.log(`\n🎬 Carousel ${index + 1} Button Analysis:`);
    
    const prevButtons = carousel.querySelectorAll('button[is="previous-button"]');
    const nextButtons = carousel.querySelectorAll('button[is="next-button"]');
    const motionList = carousel.querySelector('motion-list');
    
    console.log(`  🔍 Found ${prevButtons.length} previous buttons and ${nextButtons.length} next buttons`);
    
    if (motionList) {
      console.log(`  📏 Motion List Info:`);
      console.log(`    - Scroll Left: ${motionList.scrollLeft}px`);
      console.log(`    - Scroll Width: ${motionList.scrollWidth}px`);
      console.log(`    - Client Width: ${motionList.clientWidth}px`);
      console.log(`    - Can Scroll: ${motionList.scrollWidth > motionList.clientWidth}`);
    }
    
    prevButtons.forEach((button, btnIndex) => {
      const isDisabled = button.disabled || button.hasAttribute('disabled');
      const isHidden = button.hidden || button.style.display === 'none';
      const computedStyle = window.getComputedStyle(button);
      const hasListeners = button.dataset.hasCustomListener === 'true';
      
      console.log(`  ⬅️ Previous Button ${btnIndex + 1}:`);
      console.log(`    - Disabled: ${isDisabled}`);
      console.log(`    - Has disabled attr: ${button.hasAttribute('disabled')}`);
      console.log(`    - Hidden: ${isHidden}`);
      console.log(`    - Visibility: ${computedStyle.visibility}`);
      console.log(`    - Opacity: ${computedStyle.opacity}`);
      console.log(`    - Pointer Events: ${computedStyle.pointerEvents}`);
      console.log(`    - Z-Index: ${computedStyle.zIndex}`);
      console.log(`    - Has Custom Listener: ${hasListeners}`);
      console.log(`    - Position: ${computedStyle.position}`);
      
      // 超强制激活按钮
      button.removeAttribute('disabled');
      button.disabled = false;
      button.style.cssText += `
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        z-index: 9999 !important;
        position: relative !important;
        cursor: pointer !important;
      `;
      button.hidden = false;
      
      // 添加或重新添加事件监听器
      if (!hasListeners) {
        button.addEventListener('click', function(e) {
          console.log('🎯 Previous button clicked!');
          e.preventDefault();
          e.stopPropagation();
          
          if (motionList) {
            const currentScroll = motionList.scrollLeft;
            const scrollAmount = 340; // 固定滚动距离
            const newScroll = Math.max(currentScroll - scrollAmount, 0);
            
            console.log(`📍 Scrolling from ${currentScroll}px to ${newScroll}px`);
            motionList.scrollTo({
              left: newScroll,
              behavior: 'smooth'
            });
          }
        }, { capture: true });
        
        button.dataset.hasCustomListener = 'true';
        console.log(`    ✅ Added fresh click listener`);
      }
      
      console.log(`    ✅ Button super-activated`);
    });
    
    nextButtons.forEach((button, btnIndex) => {
      const isDisabled = button.disabled || button.hasAttribute('disabled');
      const isHidden = button.hidden || button.style.display === 'none';
      const computedStyle = window.getComputedStyle(button);
      const hasListeners = button.dataset.hasCustomListener === 'true';
      
      console.log(`  ➡️ Next Button ${btnIndex + 1}:`);
      console.log(`    - Disabled: ${isDisabled}`);
      console.log(`    - Has disabled attr: ${button.hasAttribute('disabled')}`);
      console.log(`    - Hidden: ${isHidden}`);
      console.log(`    - Visibility: ${computedStyle.visibility}`);
      console.log(`    - Opacity: ${computedStyle.opacity}`);
      console.log(`    - Pointer Events: ${computedStyle.pointerEvents}`);
      console.log(`    - Z-Index: ${computedStyle.zIndex}`);
      console.log(`    - Has Custom Listener: ${hasListeners}`);
      
      // 超强制激活按钮
      button.removeAttribute('disabled');
      button.disabled = false;
      button.style.cssText += `
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        z-index: 9999 !important;
        position: relative !important;
        cursor: pointer !important;
      `;
      button.hidden = false;
      
      // 添加或重新添加事件监听器
      if (!hasListeners) {
        button.addEventListener('click', function(e) {
          console.log('🎯 Next button clicked!');
          e.preventDefault();
          e.stopPropagation();
          
          if (motionList) {
            const currentScroll = motionList.scrollLeft;
            const scrollAmount = 340; // 固定滚动距离
            const maxScroll = motionList.scrollWidth - motionList.clientWidth;
            const newScroll = Math.min(currentScroll + scrollAmount, maxScroll);
            
            console.log(`📍 Scrolling from ${currentScroll}px to ${newScroll}px`);
            motionList.scrollTo({
              left: newScroll,
              behavior: 'smooth'
            });
          }
        }, { capture: true });
        
        button.dataset.hasCustomListener = 'true';
        console.log(`    ✅ Added fresh click listener`);
      }
      
      console.log(`    ✅ Button super-activated`);
    });
    
    // 直接测试滚动功能
    if (motionList) {
      console.log(`  🧪 Testing direct scroll functions...`);
      
      window.testPrevScroll = function() {
        const currentScroll = motionList.scrollLeft;
        const newScroll = Math.max(currentScroll - 340, 0);
        console.log(`📍 Manual prev scroll: ${currentScroll}px → ${newScroll}px`);
        motionList.scrollTo({ left: newScroll, behavior: 'smooth' });
      };
      
      window.testNextScroll = function() {
        const currentScroll = motionList.scrollLeft;
        const maxScroll = motionList.scrollWidth - motionList.clientWidth;
        const newScroll = Math.min(currentScroll + 340, maxScroll);
        console.log(`📍 Manual next scroll: ${currentScroll}px → ${newScroll}px`);
        motionList.scrollTo({ left: newScroll, behavior: 'smooth' });
      };
      
      console.log(`  🎮 Manual test functions available: testPrevScroll(), testNextScroll()`);
    }
  });
  
  console.log('\n💡 All buttons have been super-activated with fresh event listeners!');
  console.log('🔧 Try clicking the buttons now, or use testPrevScroll()/testNextScroll() for manual testing.');
  console.log('📊 Check the console for click events and scroll messages.');
};

// 测试移动端容器边距和布局
window.testMobilePadding = function() {
  console.log('📐 Testing Mobile Container Padding & Layout...');
  
  if (window.innerWidth >= 768) {
    console.log('⚠️ This test is for mobile screens (width < 768px). Current width:', window.innerWidth + 'px');
    return;
  }
  
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach((carousel, index) => {
    console.log(`\n🎬 Carousel ${index + 1} Container Analysis:`);
    
    const mobileCarousel = carousel.querySelector('.mobile-carousel');
    if (!mobileCarousel) {
      console.error('  ❌ Mobile carousel not found');
      return;
    }
    
    // 获取容器的计算样式
    const containerStyle = window.getComputedStyle(mobileCarousel);
    const leftPadding = parseInt(containerStyle.paddingLeft) || 0;
    const rightPadding = parseInt(containerStyle.paddingRight) || 0;
    
    // 获取容器尺寸信息
    const containerWidth = mobileCarousel.offsetWidth;
    const containerClientWidth = mobileCarousel.clientWidth;
    const containerRect = mobileCarousel.getBoundingClientRect();
    
    console.log('  📊 Container Dimensions:');
    console.log(`    - Screen Width: ${window.innerWidth}px`);
    console.log(`    - Container Width (including padding): ${containerWidth}px`);
    console.log(`    - Container Client Width (content area): ${containerClientWidth}px`);
    console.log(`    - Left Padding: ${leftPadding}px`);
    console.log(`    - Right Padding: ${rightPadding}px`);
    
         console.log('  🎯 Layout Analysis:');
     console.log(`    - Container Left Edge: ${containerRect.left.toFixed(1)}px (should be 0)`);
     console.log(`    - Container Right Edge: ${containerRect.right.toFixed(1)}px (should equal screen width)`);
     console.log(`    - Content Left Edge: ${containerRect.left.toFixed(1)}px (no left padding)`);
     console.log(`    - Content Right Edge: ${containerRect.right.toFixed(1)}px (no right padding)`);
    
    // 验证容器是否正确填满屏幕宽度
    const fillsScreen = Math.abs(containerWidth - window.innerWidth) <= 2;
    console.log(`    - Fills Screen Width: ${fillsScreen ? '✅ YES' : '❌ NO'}`);
    
         // 验证内容区域宽度计算（左右边距都为0）
     const expectedContentWidth = window.innerWidth; // 左右边距现在都是0
     const actualContentWidth = containerClientWidth;
     const contentWidthCorrect = Math.abs(expectedContentWidth - actualContentWidth) <= 2;
     console.log(`    - Content Width Correct: ${contentWidthCorrect ? '✅ YES' : '❌ NO'} (expected: ${expectedContentWidth}px, actual: ${actualContentWidth}px)`);
    
    // 检查Swiper slides宽度
    const slides = mobileCarousel.querySelectorAll('.swiper-slide');
    if (slides.length > 0) {
      const slideWidth = slides[0].offsetWidth;
      const slideRect = slides[0].getBoundingClientRect();
      console.log(`    - Slide Width: ${slideWidth}px`);
      console.log(`    - Slide Left Edge: ${slideRect.left.toFixed(1)}px`);
      console.log(`    - Slide Right Edge: ${slideRect.right.toFixed(1)}px`);
      
      // 验证slide是否正确使用content区域
      const slideUsesContentArea = Math.abs(slideWidth - actualContentWidth) <= 2;
      console.log(`    - Slide Uses Content Area: ${slideUsesContentArea ? '✅ YES' : '❌ NO'}`);
    }
    
    // 检查section overflow
    const section = carousel.closest('section');
    if (section) {
      const sectionStyle = window.getComputedStyle(section);
      console.log('  📋 Section Overflow Settings:');
      console.log(`    - Overflow-X: ${sectionStyle.overflowX}`);
      console.log(`    - Overflow-Y: ${sectionStyle.overflowY}`);
    }
  });
  
  console.log('\n💡 Container should fill screen width completely (no padding)');
  console.log('🎯 Content area = Screen Width (Left and Right Padding are both 0)');
  console.log('📋 If scrollbars appear, check section overflow settings');
};

// 检查滚动条问题的专用诊断工具
window.checkScrollbars = function() {
  console.log('🔍 Checking for Unwanted Scrollbars...');
  
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach((carousel, index) => {
    console.log(`\n📱 Carousel ${index + 1} Scrollbar Analysis:`);
    
    const section = carousel.closest('section');
    const videoCarousel = carousel;
    const container = carousel.querySelector('.video-carousel-container');
    const pageWidth = carousel.querySelector('.page-width');
    const mobileCarousel = carousel.querySelector('.mobile-carousel');
    
    // 检查各个层级的overflow设置
    const elements = [
      { name: 'Section', element: section },
      { name: 'Video-Carousel', element: videoCarousel },
      { name: 'Container', element: container },
      { name: 'Page-Width', element: pageWidth },
      { name: 'Mobile-Carousel', element: mobileCarousel }
    ];
    
    elements.forEach(({ name, element }) => {
      if (element) {
        const style = window.getComputedStyle(element);
        const overflowX = style.overflowX;
        const overflowY = style.overflowY;
        const scrollWidth = element.scrollWidth;
        const clientWidth = element.clientWidth;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;
        
        console.log(`  🔧 ${name}:`);
        console.log(`    - Overflow-X: ${overflowX}`);
        console.log(`    - Overflow-Y: ${overflowY}`);
        console.log(`    - Has Horizontal Scroll: ${scrollWidth > clientWidth ? '⚠️ YES' : '✅ NO'}`);
        console.log(`    - Has Vertical Scroll: ${scrollHeight > clientHeight ? '⚠️ YES' : '✅ NO'}`);
        console.log(`    - Dimensions: ${clientWidth}×${clientHeight}px (client) vs ${scrollWidth}×${scrollHeight}px (scroll)`);
        
        if (scrollWidth > clientWidth || scrollHeight > clientHeight) {
          console.log(`    🚨 ISSUE: This element may be causing scrollbars!`);
        }
      } else {
        console.log(`  ❌ ${name}: Not found`);
      }
    });
    
    // 检查body和html级别的滚动
    const body = document.body;
    const html = document.documentElement;
    
    console.log('\n🌐 Page Level Scroll Check:');
    console.log(`  📄 Body: ${body.scrollWidth}×${body.scrollHeight}px vs ${body.clientWidth}×${body.clientHeight}px`);
    console.log(`  📋 HTML: ${html.scrollWidth}×${html.scrollHeight}px vs ${html.clientWidth}×${html.clientHeight}px`);
    console.log(`  🖥️ Window: ${window.innerWidth}×${window.innerHeight}px`);
    
    const hasPageHorizontalScroll = body.scrollWidth > window.innerWidth || html.scrollWidth > window.innerWidth;
    const hasPageVerticalScroll = body.scrollHeight > window.innerHeight || html.scrollHeight > window.innerHeight;
    
    console.log(`  ➡️ Page Horizontal Scroll: ${hasPageHorizontalScroll ? '⚠️ YES' : '✅ NO'}`);
    console.log(`  ⬇️ Page Vertical Scroll: ${hasPageVerticalScroll ? '✅ NORMAL' : '⚠️ NONE'}`);
  });
  
  console.log('\n💡 Tips to fix scrollbar issues:');
  console.log('  • Set overflow: visible on section elements');
  console.log('  • Use overflow-x: hidden on mobile to prevent horizontal scroll');
  console.log('  • Ensure container widths do not exceed screen width');
  console.log('  • Check for elements with fixed widths that are too large');
};

// 测试移动端溢出问题
window.testMobileOverflow = function() {
  console.log('🔍 Testing Mobile Overflow Prevention...');
  
  if (window.innerWidth >= 768) {
    console.log('⚠️ This test is for mobile screens (width < 768px). Current width:', window.innerWidth + 'px');
    return;
  }
  
  // 检查页面是否有横向滚动条
  const hasHorizontalScroll = document.body.scrollWidth > window.innerWidth;
  const documentHasScroll = document.documentElement.scrollWidth > window.innerWidth;
  
  console.log('📊 Overflow Analysis:');
  console.log(`  🖥️ Window Width: ${window.innerWidth}px`);
  console.log(`  📄 Body Scroll Width: ${document.body.scrollWidth}px`);
  console.log(`  📋 Document Scroll Width: ${document.documentElement.scrollWidth}px`);
  console.log(`  ❓ Has Horizontal Scroll (Body): ${hasHorizontalScroll ? '❌ YES' : '✅ NO'}`);
  console.log(`  ❓ Has Horizontal Scroll (Document): ${documentHasScroll ? '❌ YES' : '✅ NO'}`);
  
  // 检查轮播容器
  const carousels = document.querySelectorAll('video-carousel');
  carousels.forEach((carousel, index) => {
    console.log(`\n🎬 Carousel ${index + 1} Container Analysis:`);
    
    const motionList = carousel.querySelector('motion-list');
    const cards = motionList ? motionList.querySelectorAll('.video-carousel-card') : [];
    
    if (motionList && cards.length > 0) {
      const containerWidth = motionList.offsetWidth;
      const cardWidth = cards[0].offsetWidth;
      const totalCardsWidth = cardWidth * cards.length;
      const computedStyle = window.getComputedStyle(carousel);
      const cardGap = parseInt(computedStyle.getPropertyValue('--card-gap')) || 8;
      const totalGapWidth = (cards.length - 1) * cardGap;
      const totalRequiredWidth = totalCardsWidth + totalGapWidth;
      
      console.log(`  📦 Container Width: ${containerWidth}px`);
      console.log(`  🎴 Card Width: ${cardWidth}px`);
      console.log(`  🔢 Total Cards: ${cards.length}`);
      console.log(`  📐 Card Gap: ${cardGap}px`);
      console.log(`  📏 Total Required Width: ${totalRequiredWidth}px (${totalCardsWidth} + ${totalGapWidth})`);
      console.log(`  🎯 Container Can Scroll: ${totalRequiredWidth > containerWidth ? '✅ YES' : '❌ NO'}`);
      
      // 检查卡片是否超出屏幕
      const cardRect = cards[0].getBoundingClientRect();
      const isCardWithinScreen = cardRect.right <= window.innerWidth;
      console.log(`  📱 First Card Within Screen: ${isCardWithinScreen ? '✅ YES' : '❌ NO'}`);
    }
  });
  
  // 总结
  if (!hasHorizontalScroll && !documentHasScroll) {
    console.log('\n🎉 SUCCESS: No page-level horizontal overflow detected!');
    console.log('✅ The carousel is properly contained within the screen.');
  } else {
    console.log('\n⚠️ WARNING: Page-level horizontal overflow detected!');
    console.log('🔧 Consider adjusting margins or card width calculations.');
  }
  
  console.log('\n💡 If overflow persists, try increasing the safety buffer in the code.');
}; 